import {Popover} from '@mui/material';
import React, {type ReactNode, useRef} from 'react';
import styled from 'styled-components';

import * as Colors from '../../../../common/css/color.styles';
import {Button} from '../../../Button';
import {
  DraggableGrow,
  DraggableHandle,
  PoppedBody,
  StyledTooltip,
} from '../../../DraggablePopups';

export const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
`;
TooltipContent.displayName = 'S.TooltipContent';

const Collapsed = styled.div<{$fullHeight?: boolean}>`
  overflow: hidden;
  cursor: pointer;
  ${props => props.$fullHeight && 'height: 100%; width: 100%;'}
`;
Collapsed.displayName = 'S.Collapsed';

const Popped = styled.div`
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.8rem;
  word-wrap: break-word;
  background-color: ${Colors.THEME.bgSurfaceRaised};
  color: light-dark(var(--moon-700), var(--moon-300));
  border: 1px solid light-dark(var(--moon-300), var(--moon-700));
`;
Popped.displayName = 'S.Popped';

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 0;
`;
Toolbar.displayName = 'S.Toolbar';

const Spacer = styled.div`
  flex: 1 1 auto;
`;
Spacer.displayName = 'S.Spacer';

type CellValueWithPopupOverProps = {
  /**
   * The collapsed content to render inline (e.g., in a table cell)
   */
  collapsed: ReactNode;
  /**
   * The tooltip content to show on hover when popover is closed
   */
  tooltip?: ReactNode;
  /**
   * Custom toolbar buttons to render on the left side
   * These are typically mode switchers or primary actions
   */
  leftToolbarButtons?: ReactNode;
  /**
   * Custom toolbar buttons to render on the right side (before the close button)
   * These are typically secondary actions like copy, download, etc.
   */
  rightToolbarButtons?: ReactNode;
  /**
   * The main popover body content to show below the toolbar
   */
  popoverBody: ReactNode;
  /**
   * Stretch the collapsed trigger to fill its container instead of sizing to
   * content. Used by tall cells that render a full chart.
   */
  fullHeight?: boolean;
};

/**
 * A general-purpose component that provides a collapsed view with tooltip and popover.
 * This component handles the interaction pattern of:
 * 1. Showing collapsed content in a table cell
 * 2. Showing a tooltip on hover
 * 3. Opening a draggable popover with toolbar and full content on click
 */
export const CellValueWithPopupOver = ({
  collapsed,
  tooltip,
  leftToolbarButtons,
  rightToolbarButtons,
  popoverBody,
  fullHeight,
}: CellValueWithPopupOverProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : ref.current);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'general-purpose-popper' : undefined;

  const onClose = () => setAnchorEl(null);

  // Suppress tooltip when popper is open
  const title = open ? '' : (tooltip ?? '');

  return (
    <>
      <StyledTooltip enterDelay={500} title={title}>
        <Collapsed ref={ref} onClick={onClick} $fullHeight={fullHeight}>
          {collapsed}
        </Collapsed>
      </StyledTooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        onClose={onClose}
        slots={{
          transition: DraggableGrow,
        }}>
        <Popped>
          <TooltipContent>
            <DraggableHandle>
              <Toolbar>
                {leftToolbarButtons}
                <Spacer />
                {rightToolbarButtons}
                <Button
                  size="small"
                  variant="ghost"
                  icon="close"
                  tooltip="Close preview"
                  onClick={e => {
                    e.stopPropagation();
                    onClose();
                  }}
                />
              </Toolbar>
            </DraggableHandle>
            <PoppedBody>{popoverBody}</PoppedBody>
          </TooltipContent>
        </Popped>
      </Popover>
    </>
  );
};
